/*
 * Copyright © 2017 - 2019 Atilika Inc. All rights reserved.
 */

import clsx from "clsx";
import {graphql, useStaticQuery} from "gatsby";
import React, {memo, useCallback, useContext, useMemo, useState} from "react";
import {Helmet} from "react-helmet";
import {createUseStyles} from "react-jss";
import CSSTransition from "react-transition-group/CSSTransition";
import ColorLogo from "../assets/images/logo-color.svg";
import UI from "./UI";
import Container from "./Container";
import Link from "./Link";
import NavigationLinks from "./NavigationLinks";
import PageContext from "./PageContext";

const useStyles = createUseStyles({
    root: {
        padding: "2rem 0",
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 10
    },
    content: {
        alignItems: "center",
        display: "flex",
        position: "relative",
        justifyContent: "space-between",
        zIndex: 100
    },
    logo: {
        // TODO: de-deup
        "& a": {
            display: "block",
            height: UI.menuHeight,
            [UI.media.lte("sm")]: {
                height: 38
            }
        },
        "& img": {
            display: "block",
            height: UI.menuHeight,
            [UI.media.lte("sm")]: {
                height: 38
            }
        }
    },
    menu: {
        display: "flex",
        flex: 1,
        justifyContent: "flex-end",
        alignItems: "center",
        "& a": {
            color: UI.colors.white.toCSS(),
            fontFamily: UI.fonts.lato,
            fontSize: "1.125rem",
            letterSpacing: 0.8,
            paddingLeft: "3rem",
            textDecoration: "none",
            textTransform: "lowercase",
            [UI.media.lte("sm")]: {
                fontSize: ".875rem"
            },
            "&:hover": {
                textDecoration: "underline"
            },
            [UI.media.only("md")]: {
                paddingLeft: "2rem"
            }
        }
    },
    mobile: {
        alignItems: "center",
        backgroundColor: UI.colors.veryLightGrey.toCSS(),
        display: "flex",
        height: 0,
        justifyContent: "center",
        left: 0,
        position: "fixed",
        top: 0,
        width: "100vw",
        overflow: "hidden",
        transition: "height .25s step-end, opacity .25s ease-in-out"
    },
    mobileActive: {
        height: "100vh"
        // opacity: 1,
        // transition: "height 0s step-end", opacity .25s ease-in-out,
    },
    languageSwitch: {
        textAlign: "right"
    },
    hamburgerWrapper: {
        alignItems: "center",
        display: "none",
        marginLeft: "1.5rem",
        flex: "0 0 22px",
        width: 22,
        overflow: "hidden",
        [UI.media.lte("sm")]: {
            display: "flex"
        }
    },
    hamburger: {
        display: "block",
        height: 20,
        margin: 0,
        position: "relative",
        transform: "rotate(0deg)",
        transition: ".5s ease-in-out",
        width: 22,
        "& span": {
            display: "block",
            position: "absolute",
            height: 2,
            width: 22,
            background: UI.colors.white.toCSS(),
            opacity: 1,
            left: 0,
            transform: "rotate(0deg)",
            transition: ".25s ease-in-out",
            "&:nth-child(1)": {
                top: 0
            },
            "&:nth-child(2), &:nth-child(3)": {
                top: 9
            },
            "&:nth-child(4)": {
                top: 18
            }
        }
    },
    hamburgerEnterExitActiveDone: {
        "& span": {
            "&:nth-child(1)": {
                top: 0,
                width: 22,
                left: 0
            },
            "&:nth-child(2)": {
                transform: "rotate(0deg)"
            },
            "&:nth-child(3)": {
                transform: "rotate(0deg)"
            },
            "&:nth-child(4)": {
                top: 18,
                width: 22,
                left: 0
            }
        }
    },
    hamburgerEnterActiveDoneExit: {
        "& span": {
            "&:nth-child(1)": {
                top: 18,
                width: 0,
                left: "50%"
            },
            "&:nth-child(2)": {
                transform: "rotate(45deg)"
            },
            "&:nth-child(3)": {
                transform: "rotate(-45deg)"
            },
            "&:nth-child(4)": {
                top: 18,
                width: 0,
                left: "50%"
            }
        }
    }
});

function Navigation() {
    const classes = useStyles();
    const {lang, path} = useContext(PageContext);

    const data = useStaticQuery(graphql`
        query PageFiles {
            allFile {
                edges {
                    node {
                        relativePath
                    }
                }
            }
        }
    `);
    const [active, setActive] = useState(false);

    const handleToggle = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>): void => {
            event.preventDefault();
            setActive(!active);
        },
        [active, setActive]
    );

    const handleLinkClick = useCallback(() => setActive(false), [setActive]);

    const paths: string[] = data.allFile.edges.map((edge: {node: {relativePath: string}}) => {
        return `/${edge.node.relativePath.replace(/\.tsx$/, "/")}`;
    });

    const switchLanguagePath = useMemo(() => {
        const base = `/${lang === "en" ? "ja" : "en"}/`;
        const inversePath = path.replace(new RegExp(`^\\/${lang}\\/`), base);

        if (paths.includes(inversePath)) {
            return inversePath;
        }

        return base;
    }, [lang, path, paths]);

    const transitionClasses = {
        enter: classes.hamburgerEnterExitActiveDone,
        enterActive: classes.hamburgerEnterActiveDoneExit,
        enterDone: classes.hamburgerEnterActiveDoneExit,
        exit: classes.hamburgerEnterActiveDoneExit,
        exitActive: classes.hamburgerEnterExitActiveDone,
        exitDone: classes.hamburgerEnterExitActiveDone
    };

    return (
        <nav className={classes.root}>
            {active ? (
                <Helmet>
                    <body data-no-scroll />
                </Helmet>
            ) : null}
            <Container>
                <div className={classes.content}>
                    <div className={classes.logo}>
                        <Link to={`/${lang}/`}>
                            <img src={ColorLogo} alt="Atilika: language + ai" />
                        </Link>
                    </div>

                    <div className={classes.menu}>
                        <NavigationLinks onVisit={handleLinkClick} type="desktop" lang={lang} />
                        <div className={classes.languageSwitch}>
                            <Link to={switchLanguagePath}>
                                {lang === "en" ? "日本語" : "English"}
                            </Link>
                        </div>
                        <CSSTransition classNames={transitionClasses} timeout={250} in={active}>
                            <div className={classes.hamburgerWrapper}>
                                <Link onClick={handleToggle} className={classes.hamburger}>
                                    <span />
                                    <span />
                                    <span />
                                    <span />
                                </Link>
                            </div>
                        </CSSTransition>
                    </div>
                </div>
            </Container>
            <div className={clsx(classes.mobile, {[classes.mobileActive]: active})}>
                <NavigationLinks onVisit={handleLinkClick} type="mobile" lang={lang} />
            </div>
        </nav>
    );
}

export default memo(Navigation);
